import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { currenciesAPI } from '../../services/api';
import { Currency } from '../../types';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  StarIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const CurrencyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currency, setCurrency] = useState<Currency | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (id) {
      loadCurrency();
    }
  }, [id]);

  const loadCurrency = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const currencyData = await currenciesAPI.getById(id);
      setCurrency(currencyData);
    } catch (err) {
      setError(t('currencies.errors.load'));
      console.error('Error loading currency:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatExchangeRate = (rate: number | string) => {
    const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    return isNaN(numericRate) ? '0.0000' : numericRate.toFixed(4);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(i18n.language || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString(i18n.language || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async () => {
    if (!currency || !window.confirm(t('currencies.actions.confirmDelete'))) {
      return;
    }

    try {
      await currenciesAPI.delete(currency.id);
      window.location.href = '/currencies';
    } catch (err) {
      setError(t('currencies.errors.delete'));
      console.error('Error deleting currency:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currency) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('common.notFound', 'Currency not found')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('common.notFoundDesc', "The currency you're looking for doesn't exist.")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/currencies"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('currencies.detail.title')}</h1>
            <p className="text-gray-600">{t('currencies.detail.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/currencies/${currency.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            {t('actions.edit', 'Edit')}
          </Link>
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            {t('actions.delete', 'Delete')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Currency Information */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              {t('currencies.detail.sections.basic')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.currencyCode')}</span>
                <div className="text-lg font-semibold text-gray-900">{currency.code}</div>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.currencyName')}</span>
                <div className="text-lg font-semibold text-gray-900 flex items-center">
                  {currency.name}
                  {currency.isBase && (
                    <StarIcon className="h-5 w-5 text-yellow-500 ml-2" title={t('currencies.labels.baseCurrency')} />
                  )}
                </div>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.currencySymbol')}</span>
                <div className="text-lg font-semibold text-gray-900">
                  {currency.symbol || t('currencies.labels.noSymbol')}
                </div>
              </div>
            </div>
          </div>

          {/* Exchange Rate Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('currencies.detail.sections.exchangeInfo')}</h3>
            
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.exchangeRate')}</span>
                <div className="text-lg font-semibold text-gray-900">
                  {formatExchangeRate(currency.exchangeRate)}
                </div>
                <p className="text-sm text-gray-500">{t('currencies.detail.labels.relativeToBase')}</p>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.baseCurrency')}</span>
                <div className="flex items-center">
                  {currency.isBase ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                  )}
                  <span className={`text-sm font-medium ${
                    currency.isBase ? 'text-green-800' : 'text-gray-600'
                  }`}>
                    {currency.isBase ? t('currencies.detail.labels.yes') : t('currencies.detail.labels.no')}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{currency.isBase ? t('currencies.detail.hints.isBase') : t('currencies.detail.hints.isNotBase')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Status and Timestamps */}
        <div className="space-y-6">
          {/* Status Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('currencies.detail.sections.statusInfo')}</h3>
            
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.status')}</span>
                <div className="flex items-center mt-1">
                  {currency.isActive ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    currency.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {currency.isActive ? t('currencies.statuses.active') : t('currencies.statuses.inactive')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{currency.isActive ? t('currencies.detail.hints.canUse') : t('currencies.detail.hints.cannotUse')}</p>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2" />
              {t('currencies.detail.sections.timestamps')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.createdAt')}</span>
                <div className="text-sm text-gray-900">{formatDateTime(currency.createdAt)}</div>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.lastUpdated')}</span>
                <div className="text-sm text-gray-900">{formatDateTime(currency.updatedAt)}</div>
              </div>
              
              {currency.deletedAt && (
                <div>
                  <span className="text-sm font-medium text-gray-700">{t('currencies.detail.labels.deletedAt')}</span>
                  <div className="text-sm text-gray-900">{formatDateTime(currency.deletedAt)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('currencies.detail.sections.quickActions')}</h3>
            
            <div className="space-y-3">
              <Link
                to={`/currencies/${currency.id}/edit`}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                {t('currencies.detail.quick.edit')}
              </Link>
              
              <button
                onClick={handleDelete}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                {t('currencies.detail.quick.delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrencyDetail;
